import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertActions,
  AlertContent,
  ColumnHeader
} from '@deque/cauldron-react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Button, TextField } from '@deque/cauldron-react';
import type { v2 } from '@deque/billing-service-client';
import { useAuthContext } from '../../../../../common/contexts/auth';
import styles from './JiraOnPremSettings.css';
import { type ErrorOrNull } from '../../../../../../typings/utils';
import ScrimmedLoader from '../../../../../common/components/ScrimmedLoader';
import { useHistory, useParams } from 'react-router-dom';
import {
  createKeyPair,
  getIntegrationConnectionById,
  updateIntegrationConnection
} from '../../../../../common/utils/integrations-client/jira/connections';
import { getIntegrationProductInfoFromSlug } from '../../../../../common/utils/get-integration-product-name-from-slug';
import { ProductSlugs } from '@deque/billing-utils';
import { useGlobalToast } from '../../../../../common/contexts/globalToast';

export interface JiraOnPremSettingsForm {
  url: string;
  consumerKey: string;
  publicKey: string;
}

export interface JiraOnPremSettingsProps {
  activeEnterprise: v2.Enterprise;
}

interface RouteParams {
  id: string;
}

function JiraOnPremSettings({ activeEnterprise }: JiraOnPremSettingsProps) {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();

  const history = useHistory();
  const { id: connection_id } = useParams<RouteParams>();

  // Get info required for making API calls
  const { user: authUser } = useAuthContext();

  // Get jira on prem settings for this enterprise
  const [jiraOnPremSettingsError, setJiraOnPremSettingsError] =
    useState<ErrorOrNull>(null);
  const [jiraOnPremSettingsLoading, setJiraOnPremSettingsLoading] =
    useState(true);
  useEffect(() => {
    async function fetchJiraOnPremSettings() {
      if (activeEnterprise && authUser) {
        try {
          setJiraOnPremSettingsLoading(true);
          let integrationConnection = await getIntegrationConnectionById({
            enterpriseId: activeEnterprise.id,
            token: authUser.token,
            connectionId: connection_id
          });

          // If there is no key pair, let's create it now
          if (integrationConnection.public_key === null) {
            integrationConnection = await createKeyPair({
              enterpriseId: activeEnterprise.id,
              token: authUser.token,
              connectionId: connection_id
            });
          }

          reset({
            url: integrationConnection.base_url || '',
            consumerKey: integrationConnection.consumer_key || '',
            publicKey: integrationConnection.public_key || ''
          });
        } catch (error) {
          const errorMessage = t('Failed to load settings.');

          setJiraOnPremSettingsError(new Error(errorMessage));
        } finally {
          setJiraOnPremSettingsLoading(false);
        }
      }
    }

    fetchJiraOnPremSettings();
  }, [activeEnterprise, authUser]);

  useEffect(() => {
    if (!jiraOnPremSettingsError) {
      return;
    }

    const toastMessage =
      jiraOnPremSettingsError.message ||
      t('Failed to load settings information.');

    setContents(toastMessage, 'caution');
  }, [jiraOnPremSettingsError]);

  const [showAlert, setShowAlert] = useState(false);

  const { handleSubmit, reset, resetField, control, formState, setValue } =
    useForm<JiraOnPremSettingsForm>();

  const onSubmit = async (data: JiraOnPremSettingsForm) => {
    if (activeEnterprise && authUser) {
      try {
        setJiraOnPremSettingsLoading(true);
        await updateIntegrationConnection({
          enterpriseId: activeEnterprise.id,
          connectionId: connection_id,
          token: authUser.token,
          body: {
            base_url: data.url,
            consumer_key: data.consumerKey
          }
        });

        goBackToIntegrationsConfiguration();
      } catch (error) {
        const errorMessage = t('Failed to save settings.');

        setJiraOnPremSettingsError(new Error(errorMessage));
      } finally {
        setJiraOnPremSettingsLoading(false);
      }
    }
  };

  const goBackToIntegrationsConfiguration = () => {
    history.push('/configuration/integrations');
  };
  const resetJiraURL = () => {
    resetField('url');
  };

  const regenerateKey = async (): Promise<void> => {
    setShowAlert(false);

    if (activeEnterprise && authUser) {
      try {
        setJiraOnPremSettingsLoading(true);

        const integrationConnection = await createKeyPair({
          enterpriseId: activeEnterprise.id,
          connectionId: connection_id,
          token: authUser.token
        });

        setValue('url', integrationConnection.base_url || '');
        setValue('consumerKey', integrationConnection.consumer_key || '');
        setValue('publicKey', integrationConnection.public_key || '');
      } catch (error) {
        const errorMessage = t('Failed to load integration information.');

        setJiraOnPremSettingsError(new Error(errorMessage));
      } finally {
        setJiraOnPremSettingsLoading(false);
      }
    }
  };

  const form = (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.textFieldGroup}>
      <Controller
        control={control}
        name="url"
        rules={{
          required: true,
          validate: value => {
            try {
              new URL(value);
              return true;
            } catch {
              return false;
            }
          }
        }}
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            fieldRef={ref}
            id="jira-url"
            aria-label={t('Jira URL')}
            label={t('Jira URL')}
            required
            requiredText={t('Required')}
            error={
              formState.errors.url ? (
                <div className={styles.errorWithReset}>
                  <Trans>
                    <span>A valid URL is required.</span>
                    <Button
                      id="reset-jira-url"
                      aria-label={t('Reset Jira URL')}
                      variant="link"
                      onClick={resetJiraURL}
                    >
                      Reset
                    </Button>
                  </Trans>
                </div>
              ) : undefined
            }
          />
        )}
      />

      <Controller
        control={control}
        name="consumerKey"
        rules={{
          required: true
        }}
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            fieldRef={ref}
            id="consumer-key"
            aria-label={t('Consumer Key')}
            label={t('Consumer Key')}
            required
            requiredText={t('Required')}
            error={
              formState.errors.consumerKey ? (
                <div className={styles.errorWithReset}>
                  <Trans>
                    <span>Consumer key is required.</span>
                  </Trans>
                </div>
              ) : undefined
            }
          />
        )}
      />

      <Controller
        control={control}
        name="publicKey"
        render={({ field: { ref, ...field } }) => (
          <TextField
            {...field}
            fieldRef={ref}
            id="public-key"
            aria-label={t('Public Key')}
            label={t('Public Key')}
            disabled={true}
            multiline
          />
        )}
      />
      <div>
        <Button
          className={styles.generateKey}
          onClick={() => setShowAlert(true)}
        >
          {t('Regenerate Key')}
        </Button>
      </div>

      <div className={styles.configurationFooter}>
        <Button disabled={false} onClick={handleSubmit(onSubmit)}>
          {t('Save')}
        </Button>
        <Button
          disabled={false}
          onClick={goBackToIntegrationsConfiguration}
          variant="secondary"
        >
          {t('Discard')}
        </Button>
      </div>
    </form>
  );

  return (
    <>
      <ColumnHeader>
        <h2>
          {t('{{productName}} Settings', {
            productName: getIntegrationProductInfoFromSlug(
              ProductSlugs.jiraOnPremIntegration
            ).displayName
          })}
        </h2>
      </ColumnHeader>

      {showAlert && (
        <Alert
          heading={t('Generate New Key')}
          onClose={() => setShowAlert(false)}
          show={showAlert}
          variant="warning"
        >
          <AlertContent>
            {t(
              'This will immediately generate a new key and permanently overwrite the current one. Are you sure you want to create a new key?'
            )}
          </AlertContent>
          <AlertActions>
            <Button variant="error" onClick={regenerateKey}>
              {t('Create new Key')}
            </Button>
            <Button variant="secondary" onClick={() => setShowAlert(false)}>
              {t('Cancel')}
            </Button>
          </AlertActions>
        </Alert>
      )}

      <h3>
        {t('{{productName}} Settings', {
          productName: getIntegrationProductInfoFromSlug(
            ProductSlugs.jiraOnPremIntegration
          ).displayName
        })}
      </h3>

      {!jiraOnPremSettingsLoading || (
        <ScrimmedLoader label={t('Loading ...')} />
      )}

      {!jiraOnPremSettingsError && !jiraOnPremSettingsLoading && form}
    </>
  );
}

export default JiraOnPremSettings;
